import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Wifi, Car, Waves, Check, ArrowRight, Home, Layout } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const StaysPage = () => {
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStays = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/stays');
        const data = await response.json();
        setStays(data);
      } catch (error) {
        console.error('Error fetching stays:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStays();
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Ophold & Lejligheder i Cádiz | Golf4Life</title>
        <meta name="description" content="Bo i autentiske 3-værelses lejligheder i La Barrosa, Cádiz. Se vores udvalg af ophold med Wi-Fi, parkering og havudsigt." />
      </Helmet>

      <Navbar solid={true} />

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 md:pt-32 md:pb-24 bg-[var(--brand)] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-semibold mb-6"
            >
              Jeres hjem i Spanien
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/80 max-w-2xl mb-8"
            >
              Vi har nøje udvalgt de bedste lejligheder i La Barrosa, så I kan nyde jeres golfophold i komfortable og autentiske rammer.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-4"
            >
              <Link 
                to="/kontakt" 
                className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 rounded-full border-2 border-white bg-transparent text-white font-semibold text-base md:text-lg hover:-translate-y-1 transition-all shadow-lg shadow-black/20 group"
              >
                Book dit ophold
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Stays List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--brand)]"></div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {stays.map((stay, index) => {
              const isSeaView = stay.name.da === 'Havudsigt Lejligheder';
              const isFirst = index === 0;
              
              return (
                <section 
                  key={stay.id} 
                  className={`py-20 md:py-32 ${isFirst ? 'bg-slate-50' : 'bg-white'}`}
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {isSeaView ? (
                      <motion.div 
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20"
                      >
                        {/* Left Column - Text */}
                        <div className="space-y-8">
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[var(--brand)] font-medium uppercase tracking-widest text-sm">
                              <MapPin size={18} />
                              {stay.location.da}
                            </div>
                            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">{stay.name.da}</h2>
                            <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                              <p>
                                For dem, der ønsker det lille ekstra, tilbyder vi spansk indrettede lejligheder med direkte udsigt over Atlanterhavet. Her vågner I op til lyden af bølgerne og kan nyde solnedgangen fra jeres egen balkon.
                              </p>
                              <p>
                                Disse lejligheder er nøje udvalgt for deres placering, så jeres ophold bliver så behageligt som muligt.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Features & List */}
                        <div className="space-y-10">
                          {/* Features Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-8 border-b border-slate-100">
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Layout size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">3 Værelser</p>
                                <p className="text-sm text-slate-500">God plads til alle</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Wifi size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">Fri Wi-Fi</p>
                                <p className="text-sm text-slate-500">Altid online</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Car size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">Parkering</p>
                                <p className="text-sm text-slate-500">Sikker parkering</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Waves size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">Havudsigt</p>
                                <p className="text-sm text-slate-500">Mulighed for tilvalg</p>
                              </div>
                            </div>
                          </div>

                          {/* Bullet Points */}
                          <ul className="space-y-4">
                            {[
                              "Panoramaudsigt over havet",
                              "Spansk indretning og hyggelig atmosfære",
                              "Store private balkoner",
                              "Tæt på de bedste restauranter i La Barrosa",
                              "Egen parkeringsplads i lukket garage"
                            ].map((item, i) => (
                              <li key={i} className="flex items-center gap-3 text-slate-700">
                                <Check size={18} className="text-black shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>

                          <div className="pt-4">
                            <Link 
                              to="/kontakt" 
                              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[var(--brand)] text-white font-medium text-lg hover:-translate-y-1 transition-all shadow-lg shadow-[var(--brand-20)] group"
                            >
                              Forhør dig om dette ophold
                              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div 
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-20 items-center`}
                      >
                        {/* Images Grid */}
                        <div className="w-full lg:w-1/2 grid grid-cols-2 grid-rows-2 gap-4 h-[300px] sm:h-[400px] md:h-[450px] lg:h-[480px]">
                          {stay.images.map((img, i) => (
                            <div 
                              key={i} 
                              className={`relative rounded-2xl overflow-hidden shadow-xl group transition-all duration-500 hover:shadow-2xl ${
                                stay.images.length === 1 ? 'col-span-2 row-span-2' : 
                                stay.images.length === 2 ? 'row-span-2' :
                                stay.images.length === 3 && i === 0 ? 'row-span-2' : ''
                              }`}
                            >
                              <img 
                                src={img} 
                                alt={stay.name.da} 
                                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                            </div>
                          ))}
                        </div>

                        {/* Content */}
                        <div className="w-full lg:w-1/2 space-y-8">
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[var(--brand)] font-medium uppercase tracking-widest text-sm">
                              <MapPin size={18} />
                              {stay.location.da}
                            </div>
                            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">{stay.name.da}</h2>
                            <div 
                              className="text-lg text-slate-600 leading-relaxed prose prose-slate max-w-none"
                              dangerouslySetInnerHTML={{ __html: stay.description.da }}
                            />
                          </div>

                          {/* Features Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-8 border-y border-slate-100">
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Layout size={20} />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">3 Værelser</p>
                                <p className="text-sm text-slate-500">God plads til alle</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Wifi size={20} />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">Fri Wi-Fi</p>
                                <p className="text-sm text-slate-500">Altid online</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Car size={20} />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">Parkering</p>
                                <p className="text-sm text-slate-500">Sikker parkering</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="text-black shrink-0 pt-1">
                                <Waves size={20} />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">Havudsigt</p>
                                <p className="text-sm text-slate-500">Mulighed for tilvalg</p>
                              </div>
                            </div>
                          </div>

                          <div 
                            className="prose prose-slate prose-li:text-slate-600 prose-li:marker:text-[var(--brand)]"
                            dangerouslySetInnerHTML={{ __html: stay.features.da }}
                          />

                          <div className="pt-4">
                            <Link 
                              to="/kontakt" 
                              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[var(--brand)] text-white font-semibold text-lg hover:-translate-y-1 transition-all shadow-lg shadow-[var(--brand-20)] group"
                            >
                              Forhør dig om dette ophold
                              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Area Info */}
        <section className="relative bg-slate-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="py-16 md:py-24 lg:pr-16 space-y-8">
                <div className="space-y-4">
                  <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">Oplev La Barrosa</h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    Vores lejligheder ligger i den charmerende kystby La Barrosa. Her finder I en af Spaniens smukkeste sandstrande, lokale fiskerestauranter og en autentisk atmosfære, som man sjældent finder i de store turistområder.
                  </p>
                </div>
                
                <ul className="space-y-4">
                  {[
                    "8 km bred sandstrand i gåafstand",
                    "Lokale markeder med friske råvarer",
                    "Kort køreafstand til de bedste golfbaner",
                    "Trygt og roligt område ideelt til longstay"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <Check size={18} className="text-black shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="pt-4">
                  <Link 
                    to="/kontakt" 
                    className="inline-flex items-center text-[var(--brand)] font-semibold hover:gap-2 transition-all group"
                  >
                    Læs mere om området
                    <ArrowRight size={20} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {/* Full-width image for desktop, standard for mobile */}
          <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 h-[400px] lg:h-auto">
            <img 
              src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/6e6fc98f-d996-4619-a2df-7c8102a25a39.png" 
              alt="La Barrosa Strand" 
              className="w-full h-full object-cover"
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StaysPage;